/**
 * How the repayment is spread across the instalments.
 *
 * The backend only ever builds one schedule — `ceil(total / n)` per
 * instalment with the rounding remainder on the last (`Loan.create_installments`)
 * — and the old Android form let a manager hand-edit every row of it. A user
 * asked for the edit back and, more usefully, for the common shapes to be
 * offered rather than typed row by row (pwa#127):
 *
 *  - **مساوی** — the backend's own schedule, untouched.
 *  - **صعودی / نزولی** — a straight ramp whose last instalment is about twice
 *    the first (or the reverse), for a borrower whose income is growing or
 *    who wants the bulk behind them early.
 *  - **رند** — every instalment a round figure and the odd part on the last:
 *    20,000,000 over 6 is 3,3,3,3,3,5 million rather than 3,333,334 × 5 +
 *    3,333,330, so the fund's own books stay in round numbers.
 *
 * Everything here keeps the total EXACT. The backend refuses a schedule whose
 * sum is off by a single toman (`utils/installments.py`), and it refuses it
 * silently: the loan is still created, with the equal split.
 */

export type SchedulePattern = 'equal' | 'ascending' | 'descending' | 'round'

export const SCHEDULE_PATTERNS: Array<{
    value: SchedulePattern
    label: string
    hint: string
}> = [
    { value: 'equal', label: 'مساوی', hint: 'همهٔ اقساط تقریباً برابر' },
    { value: 'round', label: 'رند', hint: 'اقساط رند، باقی‌مانده روی قسط آخر' },
    { value: 'ascending', label: 'صعودی', hint: 'اول کم، کم‌کم بیشتر' },
    { value: 'descending', label: 'نزولی', hint: 'اول بیشتر، کم‌کم کمتر' },
]

export const patternLabel = (pattern?: string) =>
    SCHEDULE_PATTERNS.find((item) => item.value === pattern)?.label ?? 'دلخواه'

/** the units a figure is rounded to, largest first */
const UNITS = [1_000_000, 500_000, 100_000, 50_000, 10_000, 5_000, 1_000]

/** what `Loan.create_installments` builds: `ceil(T/n)` each, the rest last */
export function equalAmounts(total: number, count: number): number[] {
    if (count <= 0) return []
    const each = Math.ceil(total / count)
    const amounts = Array(count).fill(each)
    amounts[count - 1] = total - each * (count - 1)
    return amounts
}

/**
 * «رند»: the largest round unit whose floor-of-average leaves a last
 * instalment no bigger than twice the others. 20M / 6 → 1M works (3M × 5 and
 * 5M); 20M / 12 → 1M would leave 9M on the last, so it steps down to 100k
 * (1.6M × 11 and 2.4M).
 */
export function roundAmounts(total: number, count: number): number[] {
    if (count <= 1) return equalAmounts(total, count)
    const average = total / count
    for (const unit of UNITS) {
        const base = Math.floor(average / unit) * unit
        if (base <= 0) continue
        const last = total - base * (count - 1)
        if (last <= base * 2) {
            return [...Array(count - 1).fill(base), last]
        }
    }
    return equalAmounts(total, count)
}

/**
 * «صعودی» / «نزولی»: weights 1 … 2 in equal steps, each share rounded to a
 * unit small enough (≤ 1/20 of the average) not to flatten the ramp. The
 * rounding uses largest remainders, which keeps the ramp monotone and the
 * sum exact in whole units; whatever is left under one unit (a fee of 12,345
 * تومان, say) sits on the largest instalment.
 */
export function rampAmounts(
    total: number,
    count: number,
    direction: 'ascending' | 'descending'
): number[] {
    if (count <= 1) return equalAmounts(total, count)
    const average = total / count
    const unit = UNITS.find((candidate) => candidate <= average / 20) ?? 1

    const weights = Array.from(
        { length: count },
        (_, index) => 1 + index / (count - 1)
    )
    const weightSum = weights.reduce((sum, weight) => sum + weight, 0)

    const units = Math.floor(total / unit)
    const leftover = total - units * unit

    const exact = weights.map((weight) => (units * weight) / weightSum)
    const floored = exact.map(Math.floor)
    let spare = units - floored.reduce((sum, value) => sum + value, 0)
    // hand the spare units to the largest remainders; ties go to the later
    // (larger) instalment so the ramp never dips
    const order = exact
        .map((value, index) => ({ index, remainder: value - floored[index] }))
        .sort((a, b) => b.remainder - a.remainder || b.index - a.index)
    for (const { index } of order) {
        if (spare <= 0) break
        floored[index] += 1
        spare -= 1
    }

    const amounts = floored.map((value) => value * unit)
    amounts[count - 1] += leftover
    return direction === 'ascending' ? amounts : amounts.reverse()
}

export function patternAmounts(
    pattern: SchedulePattern,
    total: number,
    count: number
): number[] {
    switch (pattern) {
        case 'round':
            return roundAmounts(total, count)
        case 'ascending':
        case 'descending':
            return rampAmounts(total, count, pattern)
        default:
            return equalAmounts(total, count)
    }
}

/**
 * The backend's draft (its dates are Jalali-aware and must not be invented
 * here) re-cut to a pattern. `equal` returns the draft itself, so the default
 * path sends exactly what it always sent.
 */
export function applyPattern(
    draft: IInstallment[],
    pattern: SchedulePattern
): IInstallment[] {
    if (pattern === 'equal' || draft.length === 0) return draft
    const sorted = [...draft].sort((a, b) => a.index - b.index)
    const total = sorted.reduce((sum, item) => sum + Number(item.amount), 0)
    const amounts = patternAmounts(pattern, total, sorted.length)
    return sorted.map((item, position) => ({
        ...item,
        amount: amounts[position],
    }))
}

export const scheduleSum = (installments: IInstallment[]) =>
    installments.reduce((sum, item) => sum + Number(item.amount || 0), 0)

/** the first instalment whose due date is not after the one before it */
export const firstDateOutOfOrder = (installments: IInstallment[]) => {
    const sorted = [...installments].sort((a, b) => a.index - b.index)
    for (let position = 1; position < sorted.length; position += 1) {
        if (sorted[position].due_date <= sorted[position - 1].due_date) {
            return sorted[position].index
        }
    }
    return undefined
}
